import { computed, Ref, SetupContext } from '@vue/composition-api';
import { UserRole } from '@front-app/services/UserApi/UserModel';
import { SelectBoxChoice } from '@front-app/components/inputs/SelectBoxModel';
import { Group } from '@front-app/services/GroupsApi/GroupModel';
import {
	canSeeAdminList,
	canSeeDeparteeList,
	canSeeExternalInviteeList,
	canSeeInviteeList,
	canSeeMemberList,
	canSeeMonitorList,
} from '@front-app/util/permission';

export type GroupMemberListRole = Exclude<UserRole, 'NONE'>;

const ORDERED_ROLES: GroupMemberListRole[] = [
	UserRole.MEMBER,
	UserRole.ADMINISTRATOR,
	UserRole.MODERATOR,
	UserRole.INVITEE,
	UserRole.EXTERNAL_INVITEE,
	UserRole.DEPARTEE,
];

const ROLE_CHOICE_LABELS: Record<GroupMemberListRole, string> = {
	[UserRole.MEMBER]: 'role_badge.member-regular',
	[UserRole.MODERATOR]: 'role_badge.moderator',
	[UserRole.ADMINISTRATOR]: 'role_badge.administrator',
	[UserRole.INVITEE]: 'role_badge.invited-timedright-user',
	[UserRole.EXTERNAL_INVITEE]: 'role_badge.invited-non-timedright-user',
	[UserRole.DEPARTEE]: 'role_badge.departee',
};

type RoleChoiceVisibilityValidator = (group: Group) => boolean;

const ROLE_CHOICE_VISIBILITY_FILTERS: Record<GroupMemberListRole, RoleChoiceVisibilityValidator> = {
	[UserRole.MEMBER]: canSeeMemberList,
	[UserRole.MODERATOR]: canSeeMonitorList,
	[UserRole.ADMINISTRATOR]: canSeeAdminList,
	[UserRole.INVITEE]: canSeeInviteeList,
	[UserRole.EXTERNAL_INVITEE]: canSeeExternalInviteeList,
	[UserRole.DEPARTEE]: canSeeDeparteeList,
};

export interface UseFilterMemberList {
	roleChoices: Ref<SelectBoxChoice[]>;
	selectedRolesLabel: Ref<string>;
}

export function useFilterMemberList(ctx: SetupContext, selectedChoice: Ref<GroupMemberListRole>, currentGroup: Ref<Group | undefined>): UseFilterMemberList {
	const roleChoices: Ref<SelectBoxChoice[]> = computed(() => ORDERED_ROLES
		.filter((role) => currentGroup.value && ROLE_CHOICE_VISIBILITY_FILTERS[role](currentGroup.value))
		.map((role) => ({
			key: role,
			label: ctx.root.$t(ROLE_CHOICE_LABELS[role]) as unknown as string,
		})));
	const selectedRolesLabel = computed(() => ctx.root.$t(ROLE_CHOICE_LABELS[selectedChoice.value]) as unknown as string);

	return {
		roleChoices,
		selectedRolesLabel,
	};
}
